import React, { useState, useEffect } from 'react';
import { getParticipantMemberships } from '../services/api';

// Read-only "view details" modal for Admin -> Participants: shows every
// Organizer / Tournament / Pool a participant belongs to, grouped by
// organizer. The backend endpoint (GET /participants/:id/memberships) is
// already scoped server-side -- a platform admin sees every organizer a
// participant touches, a regular organizer only ever sees their own
// slice -- so this component doesn't need to do any of its own
// role-based filtering; it just renders whatever comes back.
//
// Backdrop click and Escape close the modal, matching the existing
// ChangePasswordModal pattern.

function ParticipantMembershipsModal({ participant, onClose }) {
    const [memberships, setMemberships] = useState(null); // null = loading
    const [error,       setError]       = useState('');

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    useEffect(() => {
        let cancelled = false;
        setMemberships(null);
        setError('');
        getParticipantMemberships(participant.id)
            .then(res => { if (!cancelled) setMemberships(res.data); })
            .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load memberships'); });
        return () => { cancelled = true; };
    }, [participant.id]);

    const handleBackdropClick = () => onClose();

    // Group flat rows into Organizer -> Tournament -> [pool names], in the
    // order they arrived (the backend already orders by organizer name,
    // year desc, pool name).
    const grouped = [];
    if (memberships) {
        for (const row of memberships) {
            let org = grouped.find(g => g.organizer_id === row.organizer_id);
            if (!org) {
                org = { organizer_id: row.organizer_id, organizer_name: row.organizer_name, tournaments: [] };
                grouped.push(org);
            }
            let tournament = org.tournaments.find(t => t.tournament_id === row.tournament_id);
            if (!tournament) {
                tournament = {
                    tournament_id: row.tournament_id,
                    tournament_name: row.tournament_name,
                    year: row.year,
                    gender: row.gender,
                    pools: [],
                };
                org.tournaments.push(tournament);
            }
            tournament.pools.push(row.pool_name);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label={`${participant.name}'s memberships`}
        >
            <div
                className="w-full max-w-lg bg-gray-900 rounded-2xl p-8 border border-gray-800 max-h-[80vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-white">{participant.name}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white text-2xl leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <p className="text-gray-500 text-sm mb-6">{participant.email}</p>

                {memberships === null && !error && (
                    <p className="text-gray-500 text-sm">Loading…</p>
                )}

                {error && (
                    <p className="text-red-400 text-sm">{error}</p>
                )}

                {memberships !== null && !error && grouped.length === 0 && (
                    <p className="text-gray-500 text-sm">Not currently in any pools.</p>
                )}

                {grouped.map(org => (
                    <div key={org.organizer_id} className="mb-5 last:mb-0">
                        <h3 className="text-orange-400 font-bold text-sm uppercase tracking-wide mb-2">
                            {org.organizer_name}
                        </h3>
                        {org.tournaments.map(t => (
                            <div key={t.tournament_id} className="mb-3 last:mb-0 pl-3 border-l border-gray-800">
                                <p className="text-white text-sm font-medium">
                                    {t.tournament_name} <span className="text-gray-500 font-normal">({t.year}, {t.gender === 'W' ? "Women's" : "Men's"})</span>
                                </p>
                                <p className="text-gray-400 text-sm">
                                    {t.pools.join(', ')}
                                </p>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ParticipantMembershipsModal;
